import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {AuthService} from './auth.service';
import {catchError, Observable, of, throwError} from 'rxjs';
import {BrandedDomainConfiguration, Company, HospitalityLinkDocument, LandingProfile} from '../company/company.models';
import {BE_API_AUTH_URL, BE_API_URL} from '../constants/app.constants';
import {QrPosterTexts} from '../models/qr-poster-texts.model';
import {
  QrPosterTemplateState,
  QrPosterSystemTemplateOption,
  QrPosterTemplate,
} from '../models/qr-poster-template.model';
import {map} from 'rxjs/operators';
import {
  TechnicalServiceCategoryOrder,
  TechnicalServiceHeaderOrder,
  TechnicalServiceCategoriesResponse,
  TechnicalServiceTypeOrder,
  createEmptyTechnicalServiceOrder,
  normalizeTechnicalServiceOrder
} from '../utils/technical-service-categories';
import {AppMode, DEFAULT_APP_MODE} from '../models/app-mode.model';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {

  constructor(private http: HttpClient, private authService: AuthService) {}

  updateCompany(companyData: any): Observable<any> {
    return this.http
      .put(`${BE_API_AUTH_URL}/company/update`, companyData, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCompanyDetails(): Observable<Company> {
    return this.http
      .get<Company>(`${BE_API_AUTH_URL}/company-details`, { headers: this.authService.createAuthorizationHeader() })
      .pipe(
        map((company) => {
          if (!company) {
            return company;
          }
          const normalizedAppMode: AppMode = company.appMode === 'hospitality' ? 'hospitality' : DEFAULT_APP_MODE;
          this.authService.setAppMode(normalizedAppMode);
          return { ...company, appMode: normalizedAppMode };
        }),
        catchError(this.authService.handleError.bind(this.authService))
      );
  }

  listLandingProfiles(includeInactive = false): Observable<LandingProfile[]> {
    const suffix = includeInactive ? '?includeInactive=true' : '';
    return this.http
      .get<LandingProfile[]>(`${BE_API_AUTH_URL}/landing-profiles${suffix}`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  createLandingProfile(payload: Partial<LandingProfile>): Observable<LandingProfile> {
    return this.http
      .post<LandingProfile>(`${BE_API_AUTH_URL}/landing-profiles`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateLandingProfile(profileId: string, payload: Partial<LandingProfile>): Observable<LandingProfile> {
    return this.http
      .put<LandingProfile>(`${BE_API_AUTH_URL}/landing-profiles/${profileId}`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deactivateLandingProfile(profileId: string): Observable<{ success: boolean; profile: LandingProfile }> {
    return this.http
      .delete<{ success: boolean; profile: LandingProfile }>(`${BE_API_AUTH_URL}/landing-profiles/${profileId}`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getTechnicalServiceTypes(): Observable<TechnicalServiceTypeOrder> {
    return this.http
      .get<TechnicalServiceCategoriesResponse>(`${BE_API_AUTH_URL}/company/technical-service-types`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (response?.categories) {
            return normalizeTechnicalServiceOrder(response.categories);
          }
          return createEmptyTechnicalServiceOrder();
        }),
        catchError(this.authService.handleError.bind(this.authService))
      );
  }

  getQrPosterTexts(): Observable<QrPosterTexts> {
    return this.http
      .get<any>(`${BE_API_AUTH_URL}/company/qr-poster-texts`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(
        map((res) => (res?.qrPosterTexts ? res.qrPosterTexts : res)),
        catchError(this.authService.handleError.bind(this.authService))
      );
  }

  updateQrPosterTexts(qrPosterTexts: QrPosterTexts): Observable<QrPosterTexts> {
    return this.http
      .put<QrPosterTexts>(
        `${BE_API_AUTH_URL}/company/qr-poster-texts`,
        { qrPosterTexts },
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getQrPosterTemplateState(): Observable<QrPosterTemplateState> {
    return this.http
      .get<QrPosterTemplateState>(`${BE_API_AUTH_URL}/company/qr-poster-template`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(
        catchError(this.authService.handleError.bind(this.authService))
      );
  }

  getQrPosterTemplate(): Observable<QrPosterTemplate | null> {
    return this.getQrPosterTemplateState().pipe(map((response) => response?.qrPosterTemplate || null));
  }

  saveQrPosterTemplate(payload: {
    qrPosterTemplate: QrPosterTemplate;
    templateId?: string;
    name?: string;
    setActive?: boolean;
  }): Observable<QrPosterTemplateState> {
    return this.http
      .put<QrPosterTemplateState>(
        `${BE_API_AUTH_URL}/company/qr-poster-template`,
        payload,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateQrPosterTemplate(qrPosterTemplate: QrPosterTemplate): Observable<QrPosterTemplate> {
    return this.saveQrPosterTemplate({ qrPosterTemplate, setActive: true }).pipe(
      map((response) => response.qrPosterTemplate as QrPosterTemplate),
      catchError(this.authService.handleError.bind(this.authService))
    );
  }

  setActiveQrPosterTemplate(templateId: string): Observable<QrPosterTemplateState> {
    return this.http
      .put<QrPosterTemplateState>(
        `${BE_API_AUTH_URL}/company/qr-poster-template/active`,
        { templateId },
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteQrPosterTemplate(): Observable<{ success: boolean }> {
    return this.http
      .delete<{ success: boolean }>(`${BE_API_AUTH_URL}/company/qr-poster-template`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getQrPosterTemplateLandingProfileUsage(templateId: string): Observable<{ assignedLandingProfileCount: number }> {
    return this.http
      .get<{ assignedLandingProfileCount: number }>(
        `${BE_API_AUTH_URL}/company/qr-poster-template/templates/${encodeURIComponent(templateId)}/landing-profile-usage`,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  deleteQrPosterSavedTemplate(templateId: string, replacementActiveTemplateId?: string): Observable<QrPosterTemplateState> {
    const body: any = {};
    if (replacementActiveTemplateId) {
      body.replacementActiveTemplateId = replacementActiveTemplateId;
    }
    return this.http
      .delete<QrPosterTemplateState>(`${BE_API_AUTH_URL}/company/qr-poster-template/templates/${templateId}`, {
        headers: this.authService.createAuthorizationHeader(),
        body,
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getQrPosterSystemTemplates(): Observable<QrPosterSystemTemplateOption[]> {
    return this.http
      .get<{ templates: QrPosterSystemTemplateOption[] }>(`${BE_API_AUTH_URL}/company/qr-poster-template/system-templates`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(
        map((response) => response?.templates || []),
        catchError(this.authService.handleError.bind(this.authService))
      );
  }

  uploadQrPosterTemplateAsset(file: File): Observable<{ assetKey: string; fileName: string; fileType: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<{ assetKey: string; fileName: string; fileType: string }>(
        `${BE_API_AUTH_URL}/company/qr-poster-template/assets`,
        formData,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getBrandedDomain(): Observable<{ domain: BrandedDomainConfiguration | null; isBusinessPlan: boolean; isCompanyActive: boolean }> {
    return this.http
      .get<{ domain: BrandedDomainConfiguration | null; isBusinessPlan: boolean; isCompanyActive: boolean }>(
        `${BE_API_AUTH_URL}/company/branded-domain`,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.handleBrandedDomainError.bind(this)));
  }

  connectBrandedDomain(hostname: string): Observable<{ domain: BrandedDomainConfiguration | null }> {
    return this.http
      .post<{ domain: BrandedDomainConfiguration | null }>(
        `${BE_API_AUTH_URL}/company/branded-domain`,
        { hostname },
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.handleBrandedDomainError.bind(this)));
  }

  refreshBrandedDomain(): Observable<{ domain: BrandedDomainConfiguration | null }> {
    return this.http
      .post<{ domain: BrandedDomainConfiguration | null }>(
        `${BE_API_AUTH_URL}/company/branded-domain/refresh`,
        {},
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.handleBrandedDomainError.bind(this)));
  }

  disconnectBrandedDomain(): Observable<{ domain: BrandedDomainConfiguration | null }> {
    return this.http
      .delete<{ domain: BrandedDomainConfiguration | null }>(
        `${BE_API_AUTH_URL}/company/branded-domain`,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.handleBrandedDomainError.bind(this)));
  }

  private handleBrandedDomainError(error: HttpErrorResponse): Observable<never> {
    if (error.status === 401) {
      return this.authService.handleError(error);
    }

    return throwError(() => error);
  }

  getQrPosterAssetBlob(assetKey: string): Observable<Blob | null> {
    return this.http
      .get(`${BE_API_AUTH_URL}/company/qr-poster-template/assets/blob`, {
        headers: this.authService.createAuthorizationHeader(),
        responseType: 'blob',
        params: { key: assetKey },
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null);
          }
          return this.authService.handleError(error);
        })
      );
  }

  updateTechnicalServiceHeaderOrder(
    companyId: string,
    technicalServiceHeaderOrder: TechnicalServiceHeaderOrder,
    technicalServiceCategoryOrder: TechnicalServiceCategoryOrder,
    landingPageEmergencyServiceTypes: string[],
    options?: { suppressAudit?: boolean }
  ): Observable<{
    technicalServiceCategoryOrder: TechnicalServiceCategoryOrder;
    technicalServiceHeaderOrder: TechnicalServiceHeaderOrder;
    technicalServiceTypeOrder: TechnicalServiceTypeOrder;
    landingPageEmergencyServiceTypes: string[];
  }> {
    return this.http
      .put<{
        technicalServiceCategoryOrder: TechnicalServiceCategoryOrder;
        technicalServiceHeaderOrder: TechnicalServiceHeaderOrder;
        technicalServiceTypeOrder: TechnicalServiceTypeOrder;
        landingPageEmergencyServiceTypes: string[];
      }>(
        `${BE_API_AUTH_URL}/companies/${companyId}/technical-service-header-order`,
        {
          technicalServiceHeaderOrder,
          technicalServiceCategoryOrder,
          landingPageEmergencyServiceTypes,
          suppressAudit: options?.suppressAudit === true
        },
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  /** Fetch the actual logo image as a Blob */
  getCompanyLogoBlob(): Observable<Blob | null> {
    return this.http
      .get(`${BE_API_URL}/assets/company-logo`, {
        headers: this.authService.createAuthorizationHeader(),
        responseType: 'blob',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null);
          }
          return this.authService.handleError(error);
        })
      );
  }

  getLandingProfileLogoBlob(landingProfileId: string): Observable<Blob | null> {
    return this.http
      .get(`${BE_API_URL}/assets/landing-profile-logo/${encodeURIComponent(landingProfileId)}`, {
        headers: this.authService.createAuthorizationHeader(),
        responseType: 'blob',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null);
          }
          return this.authService.handleError(error);
        })
      );
  }

  uploadHospitalityLinkDocument(file: File): Observable<HospitalityLinkDocument> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post<HospitalityLinkDocument>(
        `${BE_API_AUTH_URL}/company/hospitality-links/document`,
        formData,
        { headers: this.authService.createAuthorizationHeader() }
      )
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }
}
